"use client"

import { useState } from "react"
import type { LucideIcon } from "lucide-react"
import {
  Calculator,
  FilePlus2,
  Info,
  MapPin,
  PackageSearch,
  Settings2,
  Truck,
} from "lucide-react"

import {
  AgentToolCard,
  AgentToolCategory,
} from "@/components/agents/shared/agent-tool-presentation"
import { ToolInstructionsDialog } from "@/components/agents/shared/dialogs/tool-instructions-dialog"
import { CrmStatusFallbackSettings } from "@/components/deliveries/crm-status-fallback-settings"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useSaveAgentDeliverySettingsMutation } from "@/hooks/mutations/delivery-bindings"
import { useAgentDeliverySettingsQuery } from "@/hooks/queries/delivery-bindings"
import { DELIVERY_TOOL_SELECTION_HINT, getDeliveryToolRequirement, updateDeliveryToolSettings } from "@/lib/deliveries/tool-selection"
import {
  type DeliveryScopeType,
  type DeliveryToolName,
  type DeliveryToolSettings,
} from "@/lib/deliveries/config"
import type {
  AgentDeliverySettingsView,
  SaveAgentDeliverySettingsInput,
} from "@/server-actions/delivery-bindings"

type DeliveryToolDefinition = {
  name: DeliveryToolName
  title: string
  description: string
  icon: LucideIcon
}

const DELIVERY_TOOL_DEFINITIONS: readonly DeliveryToolDefinition[] = [
  {
    name: "resolve_product_price",
    title: "Resolve product price",
    description: "Verify prices from the saved prompt or knowledge base. When disabled, delivery accepts prices entered by the agent from your offer.",
    icon: PackageSearch,
  },
  {
    name: "delivery_search_locations",
    title: "Search delivery locations",
    description: "Find carrier branches, parcel lockers, and pickup points for the customer.",
    icon: MapPin,
  },
  {
    name: "delivery_quote_shipment",
    title: "Quote delivery",
    description: "Validate the merchandise basket using the configured pricing mode and prepare a carrier quote.",
    icon: Calculator,
  },
  {
    name: "delivery_create_shipment",
    title: "Create delivery waybill",
    description: "Create one waybill from a confirmed, server-stored delivery proposal.",
    icon: FilePlus2,
  },
  {
    name: "delivery_track_shipment",
    title: "Track delivery waybill",
    description: "Read the carrier status for a known tracking number.",
    icon: Truck,
  },
]

export function AgentDeliveryToolsGroup({
  agentId,
  scopeType,
  canManage,
}: {
  agentId: string
  scopeType: Extract<DeliveryScopeType, "message_agent" | "email_agent">
  canManage: boolean
}) {
  const settingsQuery = useAgentDeliverySettingsQuery(scopeType, agentId)
  const saveMutation = useSaveAgentDeliverySettingsMutation()
  const [expandedTools, setExpandedTools] = useState<Set<DeliveryToolName>>(
    () => new Set(),
  )
  const [editingTool, setEditingTool] = useState<DeliveryToolDefinition | null>(
    null,
  )
  const [pendingTool, setPendingTool] = useState<DeliveryToolName | null>(null)

  const settings = settingsQuery.data
  if (!settings?.effectiveBinding) return null

  const handleToggle = async (name: DeliveryToolName, enabled: boolean) => {
    setPendingTool(name)
    const tools = updateDeliveryToolSettings(settings.tools, name, enabled)
    try {
      await saveMutation.mutateAsync(createSaveInput(settings, tools))
    } catch {
      // The mutation's shared query handler reports the backend error to the user.
    } finally {
      setPendingTool(null)
    }
  }

  const handleExpandedChange = (
    name: DeliveryToolName,
    expanded: boolean,
  ) => {
    setExpandedTools(current => {
      const next = new Set(current)
      if (expanded) next.add(name)
      else next.delete(name)
      return next
    })
  }

  return (
    <>
      <AgentToolCategory
        title="Delivery Tools"
        description={DELIVERY_TOOL_SELECTION_HINT}
        tone="delivery"
        icon={Truck}
      >
        {DELIVERY_TOOL_DEFINITIONS.map(tool => (
          <AgentToolCard
            key={tool.name}
            title={tool.title}
            description={tool.description}
            categoryLabel="Delivery"
            requirement={getDeliveryToolRequirement(tool.name)}
            tone="delivery"
            icon={tool.icon}
            checked={settings.tools[tool.name]}
            onCheckedChange={enabled => void handleToggle(tool.name, enabled)}
            expanded={expandedTools.has(tool.name)}
            onExpandedChange={expanded => {
              handleExpandedChange(tool.name, expanded)
            }}
            disabled={!canManage || pendingTool !== null}
            isPending={pendingTool === tool.name}
          >
            {tool.name === "delivery_create_shipment" ? (
              <CrmStatusFallbackSettings agentId={agentId} scopeType={scopeType} disabled={!canManage || pendingTool !== null} />
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setEditingTool(tool)}
              disabled={!canManage || pendingTool !== null}
            >
              <Settings2 aria-hidden="true" className="mr-2 h-4 w-4" />
              <span>Customize Instructions</span>
            </Button>
            <Alert>
              <Info aria-hidden="true" className="h-4 w-4" />
              <AlertDescription>
                Customize how AI uses this tool during conversations.
              </AlertDescription>
            </Alert>
          </AgentToolCard>
        ))}
      </AgentToolCategory>

      {editingTool ? (
        <ToolInstructionsDialog
          open
          onOpenChange={open => {
            if (!open) setEditingTool(null)
          }}
          toolName={editingTool.name}
          toolDisplayName={editingTool.title}
          scopeType={scopeType}
          scopeId={agentId}
        />
      ) : null}
    </>
  )
}

function createSaveInput(
  settings: AgentDeliverySettingsView,
  tools: DeliveryToolSettings,
): SaveAgentDeliverySettingsInput {
  const explicit = settings.explicitBinding
  if (!explicit) {
    return {
      scopeType: settings.scopeType,
      scopeId: settings.scopeId,
      routingMode: "inherit" as const,
      merchandisePaymentPolicy: "inherit_tenant_default" as const,
      deliveryPriceResponsibility:
        settings.tenantDefaultBinding?.deliveryPriceResponsibility ?? "business",
      tools,
    }
  }

  return {
    scopeType: settings.scopeType,
    scopeId: settings.scopeId,
    routingMode: explicit.state === "disabled"
      ? "disabled" as const
      : "integration" as const,
    integrationId: explicit.integrationId,
    merchandisePaymentPolicy: explicit.merchandisePaymentPolicy,
    deliveryPriceResponsibility: explicit.deliveryPriceResponsibility,
    crmSignal: explicit.crmSignal,
    crmFieldMapping: explicit.crmFieldMapping,
    tools,
  }
}
